"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
export default function PlanYourGathering() {
  return <section data-section-id="224087" id="project-inquiry" className="bg-background text-foreground py-24 px-4 md:px-8 lg:px-12 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-primary/5 blur-[120px] pointer-events-none rounded-full" />

      <div className="max-w-2xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-default text-4xl md:text-5xl font-extrabold tracking-tighter mb-4 text-primary">
            Project Inquiry
          </h2>
          <p className="font-default text-muted-foreground text-lg max-w-lg mx-auto">
            Secure your property with RM Roofing & Waterproofing. Provide your project details below for a technical assessment and comprehensive quote.
          </p>
        </div>

        <FormProvider formKey="project_inquiry_form_get_quote" formId="project_inquiry_form_get_quote" sectionName="Plan Your Gathering" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        event_date: "",
        guest_count: 20,
        event_type: "",
        special_requests: ""
      }} validationRules={{
        full_name: {
          required: "Full name is required"
        },
        email: {
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        },
        phone: {
          required: "Phone number is required",
          pattern: {
            value: /^[0-9\-\+\(\)\s]+$/,
            message: "Please enter a valid phone number"
          }
        },
        event_date: {
          required: "Preferred start date is required"
        },
        guest_count: {
          required: "Property size is required",
          min: {
            value: 2,
            message: "Minimum 2 sqm required"
          },
          max: {
            value: 10000,
            message: "Maximum 10,000 sqm allowed"
          }
        },
        event_type: {
          required: "Please select the property type"
        }
      }} submitText="Request Quote" successMessage="Inquiry Logged. Our technical team will review your structural requirements and contact you shortly.">
          <GatheringForm />
        </FormProvider>
      </div>
    </section>;
}
function GatheringForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-12 text-center border border-border/50 bg-card shadow-2xl rounded-lg backdrop-blur-sm">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-chart-2/10 text-chart-2 mb-6">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-default text-2xl font-bold mb-4 text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground mb-8 font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-mono uppercase tracking-widest text-xs border-2 border-primary hover:bg-primary hover:text-primary-foreground transition-all">
          New Project Inquiry
        </Button>
      </div>;
  }
  const minimalInputClass = "border-0 border-b border-border rounded-none bg-transparent px-0 py-3 h-auto shadow-none focus-visible:ring-0 focus-visible:border-ring text-foreground text-lg transition-colors placeholder:text-muted-foreground/30 font-default";
  return <Form onSubmit={handleSubmit} className="space-y-10 bg-card p-8 md:p-12 rounded-lg shadow-3xl border border-border/10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Contact Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className={minimalInputClass} {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[10px] mt-2 opacity-70">
                Primary contact for site access.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jsmith@construction.com" className={minimalInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+27 00 000 0000" className={minimalInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="event_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Preferred Start Date</FormLabel>
              <FormControl>
                <Input type="date" className={minimalInputClass} {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[10px] mt-2 opacity-70">
                Estimated project commencement.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="guest_count" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Approx. Area (sqm)</FormLabel>
              <FormControl>
                <Input type="number" min={2} max={10000} placeholder="250" className={minimalInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="event_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Property Type</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={`w-full appearance-none outline-none ${minimalInputClass} cursor-pointer`} {...field}>
                    <option value="" disabled className="bg-card text-muted-foreground">
                      Select Category
                    </option>
                    <option value="Residential" className="bg-card text-foreground">Residential Home</option>
                    <option value="Commercial" className="bg-card text-foreground">Commercial Office</option>
                    <option value="Industrial" className="bg-card text-foreground">Industrial Warehouse</option>
                    <option value="Retail" className="bg-card text-foreground">Retail Complex</option>
                    <option value="Other" className="bg-card text-foreground">Other Structure</option>
                  </select>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-primary">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Project Scope & Technical Notes</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe existing leaks, material preferences, or specific waterproofing requirements..." className={`min-h-[120px] resize-none ${minimalInputClass}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-mono">
          {submitError}
        </div>}

      <div className="pt-6 flex flex-col items-center text-center">
        <p className="font-mono text-[10px] text-muted-foreground mb-6 max-w-md uppercase tracking-tighter">
          By submitting, you agree to our{" "}
          <Link to="#" className="underline hover:text-ring transition-colors">
            Privacy Policy
          </Link>{" "}
          and technical service terms.
        </p>
        
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto min-w-[280px] h-14 rounded-full font-default font-bold uppercase tracking-widest text-sm bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-3 size-5" />
              Processing...
            </> : "Request Technical Quote"}
        </Button>
      </div>
    </Form>;
}